import { Menu as MenuUI } from "antd";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import style from "css/app.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { ReactComponent as Logo } from "asset/menu/logo.svg";
import { ReactComponent as ShortLogo } from "asset/menu/logo_short.svg";
import { ReactComponent as Hotdesking } from "asset/menu/hotdesking.svg";
import { ReactComponent as Meeting } from "asset/menu/meeting.svg";
import { ReactComponent as Mypage } from "asset/menu/mypage.svg";
import { ReactComponent as Address } from "asset/menu/address.svg";
import { ReactComponent as Record } from "asset/menu/record.svg";
import { ReactComponent as Log } from "asset/menu/log.svg";
import { ReactComponent as Notice } from "asset/menu/notice.svg";
import { ReactComponent as Realtime } from "asset/menu/realtime.svg";
import { ReactComponent as Background } from "asset/menu/background.svg";
import { useTranslation } from "react-i18next";

/**
 * 파일명: menu.js
 * 작성자: 권도훈
 * 작성일자: 23.04.06
 * 설명: app menu 컴포넌트
 */
const Menu = ({ isOpen, setIsOpen, setPageName }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const curPath = location.pathname.split("/");
  const curPathKey = curPath[curPath.length - 1];

  useEffect(() => {
    setSelectedKeys([curPathKey]);
  }, [curPathKey]);

  const dispatch = useDispatch();

  const payload = useSelector((state) => state.allowPage);

  const [items, setItems] = useState([]);
  const [rootSubmenuKeys, setRootSubmenuKeys] = useState([]);
  const [openKeys, setOpenKeys] = useState("");
  const [selectedKeys, setSelectedKeys] = useState("");

  const iconFormat = useCallback((id) => {
    switch (id) {
      case "mypage":
        return <Mypage />;
      case "room":
        return <Meeting />;
      case "hotdesking":
        return <Hotdesking />;
      case "address":
        return <Address />;
      case "record":
        return <Record />;
      case "rsrvMeeting":
        return <Log />;
      case "notice":
        return <Notice />;
      case "realtime":
        return <Realtime />;
      default:
        return null;
    }
  }, []);

  const getPageName = useCallback(
    (keyPath) => {
      let parCode = "";
      let key = "";
      const list = payload.pages;

      if (keyPath.length > 1) {
        list.forEach((el) => {
          if (el.MENU_URL === keyPath[0]) {
            parCode = el.MENU_ID;
          }
        });
        key = keyPath[1];
      } else {
        parCode = "0";
        key = keyPath[0];
      }

      list.forEach((el) => {
        if (el.MENU_URL === key && el.PAR_MENU_ID === parCode) {
          setPageName(el.MENU_NM);
        }
      });
    },
    [payload.pages, setPageName]
  );
  const systemtype = useSelector((state) => state.system.isOnline);

  const getItems = useCallback(() => {
    let items = [];
    let menuKeys = [];
    let list = payload.pages.filter((item) => item.DESKTOP_YN === "Y");
    if (systemtype === false) {
      const excludeUrls = ["realtime", "record"];
      list = list.filter((item) => !excludeUrls.includes(item.MENU_URL));
    }
    for (let i = 0; i < list.length; i++) {
      if (list[i].PAR_MENU_ID === "" || list[i].PAR_MENU_ID === "0") {
        const filterList = list.filter(
          (el) => el.PAR_MENU_ID !== "" && el.PAR_MENU_ID === list[i].MENU_ID
        );
        let childrenList = [];

        if (filterList.length > 0) {
          menuKeys.push(list[i].MENU_URL);

          for (let j = 0; j < filterList.length; j++) {
            childrenList.push(
              itemFormat(
                t("MENU." + filterList[j].MENU_NM),
                filterList[j].MENU_URL
              )
            );
          }
        }

        items.push(
          itemFormat(
            t("MENU." + list[i].MENU_NM),
            // list[i].MENU_NM,
            list[i].MENU_URL,
            iconFormat(list[i].MENU_URL),
            childrenList.length > 0 ? childrenList : null
          )
        );

        if (openKeys === "") {
          const location = new URL(window.location.href).pathname
            .replace(process.env.REACT_APP_CLIENT_URL + "app/", "")
            .split("/");

          if (location[0] === list[i].MENU_URL) {
            getPageName(location);
            setOpenKeys([list[i].MENU_URL]);
            setSelectedKeys([location[location.length - 1]]);
          }
        }
      }
    }
    setRootSubmenuKeys(menuKeys);
    setItems(items);
  }, [getPageName, iconFormat, openKeys, payload.pages, systemtype, t]);

  useLayoutEffect(() => {
    if (payload.pages.length > 0) {
      getItems();
    }
  }, [dispatch, getItems, payload.pages.length]);

  const itemFormat = (label, key, icon, children, type) => {
    return { key, icon, children, label, type };
  };

  const onOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);

    if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const movePage = (keyPath) => {
    let url = process.env.REACT_APP_CLIENT_URL + "app";
    const arr = keyPath.reverse();

    for (let i = 0; i < arr.length; i++) {
      url = url + "/" + arr[i];
    }

    if (url === process.env.REACT_APP_CLIENT_URL + "app/mypage/schedule") {
      navigate(url, { state: { type: "card" } });
    } else {
      navigate(url);
    }
  };

  const goMenuPage = () => {
    navigate(process.env.REACT_APP_CLIENT_URL + "app/mypage/schedule");
  };

  return (
    <div
      className={
        isOpen ? style.menu_wrapper : `${style.menu_wrapper} ${style.close}`
      }
      id="app_menu"
    >
      <Background className={style.menu_background} />

      {isOpen ? (
        <Logo
          className={`${style.menu_logo} cursor`}
          onClick={() => goMenuPage()}
        />
      ) : (
        <ShortLogo
          className={`${style.menu_logo} cursor`}
          onClick={() => goMenuPage()}
        />
      )}

      <MenuUI
        className={style.menu_container}
        mode="inline"
        openKeys={openKeys}
        selectedKeys={selectedKeys}
        onOpenChange={onOpenChange}
        onClick={({ key, keyPath }) => {
          setSelectedKeys([key]);
          movePage(keyPath);
          getPageName(keyPath);
        }}
        items={items}
        inlineCollapsed={!isOpen}
      />

      <div
        // className={style.menu_toggle}
        className={
          isOpen ? style.menu_toggle : `${style.menu_toggle} ${style.close}`
        }
      >
        {isOpen ? (
          <MenuFoldOutlined
            className="cursor"
            onClick={() => setIsOpen(false)}
          />
        ) : (
          <MenuUnfoldOutlined
            className="cursor"
            onClick={() => setIsOpen(true)}
          />
        )}
      </div>
    </div>
  );
};
export default Menu;
